import { Link } from "react-router-dom";
import { BedDouble, Users, Utensils, Wifi, TreePine, CalendarCheck, ArrowRight } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { AddressLink } from "@/components/AddressLink";
import { ZoomableImage } from "@/components/Lightbox";
import { useFemaleHome, femaleHomeStatus } from "@/hooks/useFemaleHome";

const MANOR_IMG = "/images/manor-house.jpg";
const HERO_IMG = "https://images.unsplash.com/photo-1588099246635-d6868ab08b44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxnb2xkZW4lMjBzdWJ1cmJhbiUyMGhvdXNlJTIwZXh0ZXJpb3IlMjB3YXJtJTIwc3VubGlnaHR8ZW58MHx8fHwxNzkxMzkxOTY5fDA&ixlib=rb-4.1.0&q=85";

const amenities = [
  { icon: BedDouble, label: "Comfortable shared & private rooms" },
  { icon: Users, label: "24/7 trained residential staff" },
  { icon: Utensils, label: "Nutritious daily meals" },
  { icon: CalendarCheck, label: "Structured daily programming" },
  { icon: Wifi, label: "Job-search & telehealth access" },
  { icon: TreePine, label: "Outdoor space & community outings" },
];

export default function Homes() {
  const fh = useFemaleHome();
  const fhStatus = femaleHomeStatus(fh);
  return (
    <div data-testid="homes-page">
      <section className="relative overflow-hidden bg-[#2E1065] grain">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 text-center relative">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400 mb-4">Our Homes</p>
          <MaskedLines
            className="font-display font-black text-[#FAF8F5] text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight"
            lines={["One umbrella.", "An opening sky of homes."]}
          />
          <Reveal delay={0.4}>
            <p className="mt-7 text-base sm:text-lg text-purple-200 leading-relaxed max-w-3xl mx-auto" data-testid="homes-intro">
              Jaliyah's House of Hope LLC is the umbrella — and beneath it, a growing family of ASAM 3.3
              residential group homes for men and women across Virginia. It begins in Petersburg.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28" data-testid="male-manor-section">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 border-4 border-amber-400 rounded-[2rem] rotate-1" />
              <ZoomableImage
                src={MANOR_IMG}
                alt="The Male Manor residence in Petersburg, Virginia"
                className="relative rounded-[2rem] shadow-2xl"
                imgClassName="w-full h-[420px] object-cover"
                testId="male-manor-image"
              />
              <span className="absolute top-5 left-5 bg-emerald-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg" data-testid="male-manor-status">
                Now Accepting Residents
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-3">The First Manor</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1E1B4B]" data-testid="male-manor-heading">
              The Male Manor — Petersburg, VA
            </h2>
              <AddressLink testId="homes-address-link" iconSize={15} className="mt-2 text-sm font-semibold text-slate-500 hover:text-[#5B21B6]" />
            <p className="mt-5 text-base text-slate-700 leading-relaxed">
              Our flagship residence delivers ASAM Level 3.3 clinically managed, high-intensity care in a true
              home setting. Men receive 24/7 staffed support, individualized recovery planning, life-skills
              development, and a structured daily rhythm designed for residents who benefit from a slower,
              more supported pace of treatment.
            </p>
            <Link
              to="/contact"
              data-testid="male-manor-referral-btn"
              className="mt-7 inline-flex items-center gap-2 bg-[#5B21B6] hover:bg-[#2E1065] text-white font-bold px-7 py-3.5 rounded-full shadow-lg shadow-purple-500/25 transition-colors duration-300"
            >
              Refer to the Male Manor <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {amenities.map((a, i) => (
            <Reveal key={a.label} delay={i * 0.06}>
              <div className="flex items-center gap-4 bg-white border border-purple-100 rounded-2xl px-5 py-4 shadow-sm" data-testid={`amenity-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#FEF3C7] flex items-center justify-center shrink-0">
                  <a.icon size={18} className="text-[#B45309]" />
                </div>
                <p className="text-sm font-semibold text-[#1E1B4B]">{a.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-gradient-to-br from-[#5B21B6] to-[#2E1065] py-20 lg:py-28 relative grain" data-testid="female-home-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className={`inline-block text-xs font-bold px-4 py-2 rounded-full mb-5 ${fhStatus.tone === "live" ? "bg-emerald-500 text-white" : "bg-amber-100 text-amber-700"}`} data-testid="female-home-status">
              {fhStatus.label}
            </span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white" data-testid="female-home-heading">
              The Female Home
            </h2>
            <p className="mt-2 text-sm font-semibold text-purple-200" data-testid="female-home-location">
              {fh.announced && fh.location ? fh.location : "Location to be announced — Virginia"}
            </p>
            {fh.announced && fh.opening_date && (
              <p className="mt-1 text-sm font-semibold text-amber-300" data-testid="female-home-opening">Opening {fh.opening_date}</p>
            )}
            <p className="mt-5 text-base text-purple-100 leading-relaxed max-w-lg">
              Women in recovery deserve the same safe harbor. Our second residence will bring the full ASAM 3.3
              model — 24/7 staffed, clinically managed, trauma-informed — to a dedicated home for women.
              The umbrella keeps opening.
            </p>
            {fh.announced && fh.note && (
              <p className="mt-4 text-sm text-amber-100 leading-relaxed max-w-lg bg-white/10 border border-amber-400/30 rounded-2xl px-5 py-4" data-testid="female-home-note">
                {fh.note}
              </p>
            )}
            <Link
              to="/contact"
              data-testid="female-home-interest-btn"
              className="mt-7 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-7 py-3.5 rounded-full shadow-xl shadow-amber-500/20 transition-colors duration-300"
            >
              {fh.accepting ? "Refer to the Female Home" : "Join the interest list"} <ArrowRight size={18} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-[2rem] overflow-hidden shadow-2xl h-[380px] border-4 border-amber-400/60">
              <img src={HERO_IMG} alt="A bright home in warm morning light" className="w-full h-full object-cover" data-testid="female-home-image" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
